// src/pages/new-analysis/components/VideoScanInterface.jsx

import React, { useRef, useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { getPresignedVideoUrl, startVideoProcessing } from "../../../lib/api";

/**
 * VideoScanInterface
 * - NEW UI (premium card)
 * - OLD working logic kept 1:1 (including webm-blob upload + 1500ms delay)
 */
const VideoScanInterface = ({
  patientName,
  notes,
  analysisDate,
  onComplete,
  onCancel,
}) => {
  const inputRef = useRef(null);

  const [videoFile, setVideoFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [statusText, setStatusText] = useState(
    "Select a recorded scalp video to begin analysis."
  );
  const [error, setError] = useState(null);

  /* ------------------------------- VALIDATION -------------------------------- */
  const validateVideo = (file) => {
    const validTypes = ["video/mp4", "video/quicktime", "video/webm"];
    const maxSize = 500 * 1024 * 1024; // 500MB

    if (!file) return "No file selected";
    if (!validTypes.includes(file.type)) return "Supported formats: MP4, MOV, WEBM";
    if (file.size > maxSize) return "Video size must be under 500MB";

    return null;
  };

  /* ------------------------------- FILE SELECT -------------------------------- */
  const handleFileSelect = (file) => {
    setError(null);

    const validationError = validateVideo(file);
    if (validationError) {
      setError(validationError);
      setStatusText(validationError);
      return;
    }

    setVideoFile(file);
    setStatusText("Video ready. You may start the analysis.");
  };

  /* ------------------------------- MAIN FLOW -------------------------------- */
  const startAnalysis = async () => {
    if (!videoFile || isUploading) return;

    setIsUploading(true);
    setError(null);

    try {
      setStatusText("Preparing secure upload…");

      // OLD: getPresignedVideoUrl() called exactly like before
      const { uploadUrl, videoKey } = await getPresignedVideoUrl();

      // OLD: MediaRecorder-compatible behavior (force webm)
      const blob = new Blob([videoFile], { type: "video/webm" });

      setStatusText("Uploading video to secure storage…");

      // OLD: fetch PUT with body only (no headers)
      const uploadRes = await fetch(uploadUrl, {
        method: "PUT",
        body: blob,
      });

      if (!uploadRes.ok) {
        throw new Error("Video upload failed.");
      }

      // OLD: delay 1500ms
      await new Promise((r) => setTimeout(r, 1500));

      setStatusText("Starting AI video analysis…");

      // OLD: startVideoProcessing(videoKey)
      const { jobId } = await startVideoProcessing(videoKey);

      // OLD: onComplete payload shape preserved exactly
      onComplete?.({
        jobId,
        patientName,
        notes,
        analysisDate,
        videoKey,
      });
    } catch (err) {
      console.error("Video analysis error:", err);
      setError(err.message || "Video analysis failed.");
      setStatusText("An error occurred.");
      setIsUploading(false);
    }
  };

  /* ------------------------------- UI -------------------------------- */
  const fileName = videoFile?.name || "No file selected";

  return (
    <main className="pt-14 px-6 pb-24 flex justify-center">
      <div className="w-full max-w-3xl">
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div className="space-y-1">
            <h1 className="text-3xl text-white font-light tracking-tight">
              Video Graft Analysis
            </h1>
            <p className="text-sm text-white/55 max-w-xl">
              Upload a recorded scalp video. Our AI analyzes graft distribution across frames
              with medical-grade precision.
            </p>
          </div>

          <button
            onClick={onCancel}
            className="text-sm text-white/60 hover:text-white transition"
          >
            Back
          </button>
        </div>

        {/* Card */}
        <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-xl shadow-[0_30px_80px_-40px_rgba(0,0,0,0.8)]">
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/[0.06] to-transparent" />
          <div className="pointer-events-none absolute -top-24 left-1/2 h-48 w-[520px] -translate-x-1/2 rounded-full bg-cyan-500/10 blur-3xl" />

          <div className="relative p-10">
            {/* Icon */}
            <div className="flex justify-center mb-8">
              <div className="h-16 w-16 rounded-2xl border border-white/15 bg-white/10 shadow-inner flex items-center justify-center">
                <Icon name="Video" size={28} color="white" />
              </div>
            </div>

            {/* Upload area */}
            <div className="mx-auto max-w-xl">
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={isUploading}
                className="w-full rounded-2xl border border-white/12 bg-white/[0.03] px-6 py-6 text-left hover:bg-white/[0.05] hover:border-white/20 transition disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-cyan-400/40"
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-sm text-white/90 font-medium">Upload video</p>
                    <p className="mt-1 text-xs text-white/55 truncate">{fileName}</p>
                  </div>

                  <div className="shrink-0 inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/[0.04] px-4 py-2">
                    <Icon name="Upload" size={16} className="text-white/80" />
                    <span className="text-xs text-white/80">Choose file</span>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-3 text-[11px] text-white/45">
                  <span className="rounded-full border border-white/10 bg-white/[0.02] px-3 py-1">
                    MP4 / MOV / WEBM
                  </span>
                  <span className="rounded-full border border-white/10 bg-white/[0.02] px-3 py-1">
                    Steady + even light
                  </span>
                  <span className="rounded-full border border-white/10 bg-white/[0.02] px-3 py-1">
                    No blur / shadow
                  </span>
                </div>
              </button>

              <input
                ref={inputRef}
                type="file"
                accept="video/mp4,video/quicktime,video/webm"
                onChange={(e) => handleFileSelect(e.target.files?.[0])}
                disabled={isUploading}
                className="hidden"
              />

              {/* Status */}
              <div className="mt-4 flex items-center justify-between gap-4">
                <p className="text-xs text-white/50">{statusText}</p>
                {videoFile && !error && (
                  <span className="text-[11px] text-cyan-200/80">Ready</span>
                )}
              </div>

              {/* Error */}
              {error && (
                <div className="mt-4 rounded-2xl border border-red-500/25 bg-red-500/10 px-4 py-3">
                  <p className="text-sm text-red-200/90">{error}</p>
                </div>
              )}

              {/* CTA */}
              <div className="mt-8 flex justify-center">
                <Button
                  onClick={startAnalysis}
                  disabled={!videoFile || isUploading}
                  className="px-10 py-4 rounded-full bg-cyan-500 text-white shadow-lg shadow-cyan-500/30 hover:bg-cyan-400 transition disabled:opacity-50"
                >
                  <Icon
                    name={isUploading ? "Loader2" : "Zap"}
                    size={18}
                    className={`mr-2 ${isUploading ? "animate-spin" : ""}`}
                  />
                  {isUploading ? "Processing…" : "Start Video Analysis"}
                </Button>
              </div>

              <p className="mt-6 text-center text-[11px] text-white/40">
                Keep the scan area sharp and evenly lit.
              </p>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default VideoScanInterface;
